'use client'

import Link from 'next/link'
import { useCallback, useRef, useState } from 'react'
import { motion, useScroll, useSpring, useTransform } from 'framer-motion'
import { Montserrat, Roboto } from 'next/font/google'
import { Modal } from '@/Modal'
import { Button } from '@/General/Button'
import { AddToCartCta } from '../_shared/AddToCartCta'
import type { CustomPageProps } from '../_shared/types'

const montserrat = Montserrat({ subsets: ['latin'], weight: ['400', '700'] })
const roboto = Roboto({ subsets: ['latin'], weight: ['400', '700'] })

const EMBED_BASE = 'https://iframe.mediadelivery.net/embed/113993'

const VIDEOS = [
  { id: 'dab3878f-f25b-42f9-8d5f-30a8fd2b6c3d', title: 'Audio Demo' },
  { id: '5f72250c-f9c9-46c9-93b4-a65157f9eb75', title: 'Graphics Demo' },
  { id: 'a77896db-aba7-4216-a4be-2c673528ccfd', title: 'Camera Demo' },
  { id: '8660c213-6a97-4b4d-ade7-b6d18fb51dc0', title: 'Customization Demo' },
  { id: '9473300b-f989-46d7-9253-3e5e5d1c9320', title: 'Dynamic Cues Demo' },
]

function FadeIn({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6 }}
    >
      {children}
    </motion.div>
  )
}

/** Legacy `btn-pulse` — subtle scale breathing on primary CTAs */
function PulseCta({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      className="inline-flex w-full justify-center sm:w-auto"
      animate={{ scale: [1, 1.03, 1] }}
      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
    >
      {children}
    </motion.div>
  )
}

function ParallaxCustomImage() {
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  })
  const y = useSpring(useTransform(scrollYProgress, [0, 1], [0, 50]), {
    stiffness: 100,
    damping: 30,
  })

  return (
    <div ref={ref}>
      <motion.img
        src="https://vinc.gumlet.io/gallery/productpages/magiccue/customisable.jpg"
        alt="Fully Customisable"
        className="w-full rounded shadow"
        style={{ y }}
      />
    </div>
  )
}

export function MagicCuePageClient({
  productId,
  productName,
  options,
  hasDownloads,
  isPreorder,
  preorderReleaseDate,
}: CustomPageProps) {
  const [videoOpen, setVideoOpen] = useState(false)
  const [activeVideo, setActiveVideo] = useState<{
    id: string
    title: string
  } | null>(null)

  const openVideo = useCallback((id: string, title: string) => {
    setActiveVideo({ id, title })
    setVideoOpen(true)
  }, [])

  const closeVideo = useCallback(() => {
    setVideoOpen(false)
    setActiveVideo(null)
  }, [])

  const embedSrc = activeVideo
    ? `${EMBED_BASE}/${activeVideo.id}?autoplay=true&preload=true`
    : ''

  const cta = (label: string) => (
    <PulseCta>
      <AddToCartCta
        productId={productId}
        productName={productName}
        options={options}
        hasDownloads={hasDownloads}
        isPreorder={isPreorder}
        preorderReleaseDate={preorderReleaseDate}
        label={label}
      />
    </PulseCta>
  )

  return (
    <div className={`bg-white text-[1.06rem] md:text-[1.12rem] leading-relaxed text-gray-800 ${roboto.className}`}>
      <section
        className="py-10 md:py-12"
        style={{
          backgroundImage:
            "url('https://vinc.gumlet.io/gallery/productpages/magiccue/background-1.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            <FadeIn className="md:col-span-8 bg-[#fffbf8] p-6 rounded shadow-sm">
              <h1
                className={`${montserrat.className} text-3xl md:text-4xl font-bold text-gray-900 mb-4`}
              >
                A Full Production Team <br />
                In Your Pocket
              </h1>
              <p className="mb-3">
                Having multimedia cues in your show is one of the easiest ways to elevate
                the experience for your audience. Yet, in the past, it was often a
                difficult, expensive, and overwhelming task.
              </p>
              <p className="mb-3">
                That all changes with Magic Cue. Seamlessly manage all your audio, video,
                and image cues with your iPhone or iPad. You can even turn your phone into a
                live camera feed that broadcasts to the audience with just one click.
              </p>
              <p className="mb-3">
                This isn&apos;t just a way to store cues though. You can also organise and
                edit them on the fly, add delays, create loops, and so much more.
                It&apos;s like having a full production team in your pocket.
              </p>
              <p className="mb-3">
                From pre‑show to post‑show, Magic Cue is the all‑in‑one solution you&apos;ve
                been searching for. Designed for pro magicians, by pro magicians, Magic Cue
                is the user‑friendly, affordable and robust solution for every performer
                from amateurs to professionals.
              </p>
              <p className="mb-3">Instantly elevate the quality of your show. Get Magic Cue today!</p>
              <div className="mt-4">{cta('Buy Now')}</div>
            </FadeIn>

            <FadeIn className="md:col-span-4">
              <div className="callout-box bg-gradient-to-br from-[#2a2a2a] to-[#1a1a1a] text-white p-6 rounded shadow-lg">
                <ul className="list-disc pl-5 space-y-2 text-base md:text-lg">
                  <li>Easiest way to add music, images, video, and live cameras to your show</li>
                  <li>Organise, arrange, edit, and control all cues in one app</li>
                  <li>Broadcast to speakers, monitors, video projectors, or LED walls</li>
                  <li>Turn your phone into a live camera feed with one click</li>
                  <li>Wireless and wired outputs</li>
                  <li>
                    Trigger cues with the included Flic button, iPhone, iPad, Apple Watch or
                    bluetooth remotes
                  </li>
                  <li>Compatible with Audio Ape, DSAN, and others</li>
                  <li>One‑time purchase (no monthly subscription)</li>
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="w-full">
        <img
          src="https://vinc.gumlet.io/gallery/productpages/magiccue/every-cue.png"
          alt="Every Cue. One App."
          className="w-full h-auto"
        />
      </section>

      <section className="py-10 bg-white text-gray-900">
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <FadeIn>
              <img
                src="https://vinc.gumlet.io/gallery/productpages/magiccue/audio.jpg"
                alt="Audio"
                className="w-full rounded shadow mb-3 threecol-img transition-transform hover:scale-[1.03]"
              />
              <Button
                type="button"
                color="blue"
                size="lg"
                className="w-full mb-4 justify-center"
                onClick={() =>
                  openVideo('dab3878f-f25b-42f9-8d5f-30a8fd2b6c3d', 'Audio Demo')
                }
              >
                ▶ Play Audio demo video
              </Button>
              <h3 className={`${montserrat.className} text-2xl font-bold mb-3`}>Audio</h3>
              <ul className="list-disc pl-5 space-y-2 text-base md:text-[1.05rem]">
                <li>Easily add music cues and sound effects</li>
                <li>Have up to 99 tracks playing simultaneously</li>
                <li>Play different sound effects seamlessly over music</li>
                <li>
                  Control how a track ends (auto‑continue to next track or pause and wait
                  for next trigger)
                </li>
                <li>
                  Add in automatic fades or even controllable fades that can be activated
                  with a button push
                </li>
                <li>Apply fades to single tracks or multiple tracks simultaneously</li>
                <li>Add delays or loops, change volume levels, and even trim clips</li>
              </ul>
            </FadeIn>

            <FadeIn>
              <img
                src="https://vinc.gumlet.io/gallery/productpages/magiccue/video.jpg"
                alt="Videos & Graphics"
                className="w-full rounded shadow mb-3 threecol-img transition-transform hover:scale-[1.03]"
              />
              <Button
                type="button"
                color="blue"
                size="lg"
                className="w-full mb-4 justify-center"
                onClick={() =>
                  openVideo('5f72250c-f9c9-46c9-93b4-a65157f9eb75', 'Graphics Demo')
                }
              >
                ▶ Play graphics demo video
              </Button>
              <h3 className={`${montserrat.className} text-2xl font-bold mb-3`}>
                Videos &amp; Graphics
              </h3>
              <ul className="list-disc pl-5 space-y-2 text-base md:text-[1.05rem]">
                <li>Easily add images, video, and text</li>
                <li>Adjust the size, position, and even layer of each track</li>
                <li>Overlay text and images over videos</li>
                <li>Ideal companion for those who do keynote speeches and lectures</li>
              </ul>
            </FadeIn>

            <FadeIn>
              <img
                src="https://vinc.gumlet.io/gallery/productpages/magiccue/live-camera.jpg"
                alt="Live Camera"
                className="w-full rounded shadow mb-3 threecol-img transition-transform hover:scale-[1.03]"
              />
              <Button
                type="button"
                color="blue"
                size="lg"
                className="w-full mb-4 justify-center"
                onClick={() =>
                  openVideo('a77896db-aba7-4216-a4be-2c673528ccfd', 'Camera Demo')
                }
              >
                ▶ Play camera demo video
              </Button>
              <h3 className={`${montserrat.className} text-2xl font-bold mb-3`}>
                Live Camera
              </h3>
              <ul className="list-disc pl-5 space-y-2 text-base md:text-[1.05rem]">
                <li>Simplest live camera utility on the market today</li>
                <li>
                  Instantly transform your iPhone or iPad camera into a live broadcast feed
                </li>
                <li>Connect to a screen via HDMI hard wired or wirelessly</li>
                <li>Use the forward or rear camera</li>
                <li>Adjust the size and position of the video, or add text overlays</li>
                <li>Take your phone into the audience or have an audience member hold it</li>
                <li>
                  Optional live video zoom feature allows you to have the camera
                  automatically follow you
                </li>
              </ul>
            </FadeIn>
          </div>
        </div>
      </section>

      <section
        className="py-12 text-white text-center"
        style={{
          backgroundImage:
            "url('https://vinc.gumlet.io/gallery/productpages/magiccue/background-3.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <FadeIn>
          <div className="max-w-screen-xl mx-auto px-4">
            <h3 className={`${montserrat.className} text-2xl md:text-3xl font-bold mb-6`}>
              Take your Show On the Go.
              <br />
              Get Magic Cue Today!
            </h3>
            {cta('Buy Now')}
          </div>
        </FadeIn>
      </section>

      <section className="py-10 bg-white">
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <FadeIn className="md:col-span-8">
              <h3 className={`${montserrat.className} text-2xl font-bold mb-4`}>
                Fully Customizable
              </h3>
              <p className="mb-3">
                Magic Cue transforms your iPhone or iPad into a pocket‑sized production
                team. This all‑in‑one solution allows you to store, access, and edit every
                cue right within the app itself.
              </p>
              <p>
                Need to trim a music cue or add a fade? Want to make a video slightly
                smaller or add text over it? This simple system enables you to arrange and
                modify cues in seconds.
              </p>
            </FadeIn>
            <FadeIn className="md:col-span-4 md:pl-4">
              <ParallaxCustomImage />
              <Button
                type="button"
                color="blue"
                size="lg"
                className="w-full mt-8 mb-4 justify-center"
                onClick={() =>
                  openVideo(
                    '8660c213-6a97-4b4d-ade7-b6d18fb51dc0',
                    'Customization Demo'
                  )
                }
              >
                ▶ Play demo video
              </Button>
            </FadeIn>
          </div>
        </div>
      </section>

      <section
        className="py-10"
        style={{
          backgroundImage:
            "url('https://vinc.gumlet.io/gallery/productpages/magiccue/background-4.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <FadeIn className="md:col-span-8 text-gray-900">
              <h3 className={`${montserrat.className} text-2xl font-bold mb-4`}>
                Be Prepared for Any Situation with Dynamic Cues
              </h3>
              <p className="mb-3">
                Booker asked you to do 10 extra minutes at the last minute? Someone drops a
                glass in the middle of a show? Dynamic Cues have you covered.
              </p>
              <p className="mb-3">
                This special feature within Magic Cue allows you to prepare single tracks,
                or groups of tracks, that can be inserted anywhere in your show on‑the‑fly
                with a push of a button.
              </p>
              <p className="mb-3">
                Dynamic Cues can even be paired to a separate remote that can be left next to
                an extra routine in your case. If you end up needing that routine, just push
                the button as you grab the props and all the cues will be automatically
                inserted.
              </p>
              <p className="mb-3">
                Someone breaks a glass during your show? With one click, you can also
                trigger a sound effect or image that plays into the funny moment.
              </p>
              <p className="mb-3">
                Once the Dynamic Cue is complete, the app will automatically transition back
                into your normal show right where you left off.
              </p>
              <p>
                Magic Cue enables a level of spontaneity not found in other show control
                solutions. You&apos;ll always be prepared for any situation.
              </p>
            </FadeIn>
            <FadeIn className="md:col-span-4 md:pl-4">
              <img
                src="https://vinc.gumlet.io/gallery/productpages/magiccue/dynamic.jpg"
                alt="Dynamic Cues"
                className="w-full rounded shadow"
              />
              <Button
                type="button"
                color="blue"
                size="lg"
                className="w-full my-4 justify-center"
                onClick={() =>
                  openVideo(
                    '9473300b-f989-46d7-9253-3e5e5d1c9320',
                    'Dynamic Cues Demo'
                  )
                }
              >
                ▶ Play Dynamic Cues video
              </Button>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="max-w-screen-xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <FadeIn className="md:col-span-8">
              <h3 className={`${montserrat.className} text-2xl font-bold mb-4`}>
                Flic Button Included
              </h3>
              <p className="mb-3">
                In addition to compatibility with Apple Watches, keyboard hotkeys, and a
                variety of other show remotes, Magic Cue proudly features native
                compatibility with the world‑class Flic buttons.
              </p>
              <p className="mb-3">
                Offering the perfect snappy tactile feel and an indoor range of up to 50
                meters (164ft), these lightweight, reliable Bluetooth Flic buttons are the
                perfect companion for Magic Cue.
              </p>
              <p className="mb-3">
                Every Magic Cue comes with one Flic Button and a custom button holder. If you
                want additional ones, they are easily available and affordable.
              </p>
              <p className="mb-1">Keep them in your pocket.</p>
              <p className="mb-1">Hide them around the stage.</p>
              <p className="mb-3">Or even attach them to props.</p>
              <p>
                Flic and Magic Cue are a match made in heaven. And you can integrate up to
                eight different buttons in seconds!
              </p>
            </FadeIn>
            <FadeIn className="md:col-span-4 md:pl-4">
              <img
                src="https://vinc.gumlet.io/gallery/productpages/magiccue/flic-black.jpg"
                alt="Flic Button"
                className="w-full rounded shadow"
              />
            </FadeIn>
          </div>
        </div>
      </section>

      <section
        className="py-12 text-white"
        style={{
          backgroundImage:
            "url('https://vinc.gumlet.io/gallery/productpages/magiccue/background-3.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <FadeIn>
          <div className="max-w-screen-xl mx-auto px-4 text-center">
            <h3 className={`${montserrat.className} text-2xl font-bold mb-6`}>
              Get Access Now
            </h3>
            <p className="mb-4 text-left max-w-3xl mx-auto text-base md:text-lg leading-relaxed">
              After purchase, your Vanishing Inc. account will be activated for Magic Cue.
              You will need to download the Magic Cue from the Apple App Store, and use your
              Vanishing Inc. login for access. The app can only be installed on one device
              at any one time. You can change the device that you use in your Vanishing Inc.
              account area.
            </p>
            <p className="mb-4 text-left max-w-3xl mx-auto text-base md:text-lg leading-relaxed">
              Vanishing Inc. is an authorised reseller of Magic Cue. One time purchase means
              that you will have access to the core show control features and any relevant
              updates for as long as they are made available by the creators iQ Stage
              Solutions LTD.
            </p>
            <p className="mb-8 text-left max-w-3xl mx-auto text-base md:text-lg leading-relaxed">
              As the app evolves, some additional features may be added. In some cases,
              these new features may require an additional fee to access.
            </p>
            <div className="flex justify-center">{cta('Get Magic Cue')}</div>
          </div>
        </FadeIn>
      </section>

      <div className="max-w-screen-xl mx-auto px-4 pt-6 pb-10">
        <p>
          Useful reading:{' '}
          <Link
            href="/magic-apps/magic-cue/what-are-show-control-systems"
            className="text-blue-600 underline hover:text-blue-800"
          >
            What are Show Control Systems?
          </Link>
        </p>
      </div>

      <Modal
        isOpen={videoOpen}
        onClose={closeVideo}
        maxWidth="6xl"
        title={activeVideo?.title ?? 'Magic Cue Demo'}
        className="overflow-hidden p-0"
        backdropOpacity="dark"
      >
        <div className="p-4 md:p-6">
          <div className="relative w-full overflow-hidden rounded-lg shadow-lg ring-1 ring-black/10">
            <div className="relative pt-[56.25%]">
              {activeVideo && (
                <iframe
                  key={activeVideo.id}
                  src={embedSrc}
                  className="absolute inset-0 h-full w-full"
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                  allowFullScreen
                  title={activeVideo.title}
                />
              )}
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-3 shadow-inner">
              <div className="flex flex-wrap justify-center gap-2">
                {VIDEOS.map(v => (
                  <Button
                    key={v.id}
                    type="button"
                    color="blue"
                    outline={activeVideo?.id !== v.id ? true : undefined}
                    size="md"
                    className="rounded-full px-4 text-sm"
                    onClick={() => openVideo(v.id, v.title)}
                  >
                    {v.title}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}
